import { MeetingDetails } from './../../../main/domain/data/meeting-details';
import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';

import { IdInterface, OrderClientRelation, ProductInterface, SelectedProductInterface } from '../data';
import { OrderApiRepository } from '../repositories/order-api.repository';
import { ProductApiRepository } from '../repositories/product-api.repository';
import { ProductCacheRepository } from '../repositories/product-cache.repository';
import { ProductDetailCacheRepository } from '../repositories/product-detail-cache.repository';
import { SelectedProductCacheRepository } from '../repositories/selected-product-cache.repository';
import { Transaction } from '../transaction/transaction';
import { ProductsByOrdertype } from 'src/app/main/domain/data/productsbyordertype';
import { ProductsbyOrderTypeInterface } from '../data/productsbyordertype.interface';
import { ProductByOrderTypeCacheRepository } from '../repositories/productbyordertype-cache.repository';

const selectedProductsMap = (provider: ProductProvider) =>
    switchMap((transaction: Transaction<SelectedProductInterface[]>): Observable<Transaction<SelectedProductInterface[]>> => {
        return transaction.map(
            (selectedProducts) =>
                provider.find().pipe(
                    map((transactionFind) => {
                        selectedProducts.forEach((selectedProduct) => {
                            const product = transactionFind.data.find(
                                (it) => String(it.encryptedCode) === String(selectedProduct.encryptedCode)
                            );

                            if (product) {
                                selectedProduct.lastPrice = product.lastPrice;
                                selectedProduct.lastPriceDate = product.lastPriceDate;
                                selectedProduct.productSpecification = product.productSpecification;
                            }

                            if (selectedProduct.order) {
                                const countHolders: number = selectedProduct.order.clients.filter(
                                    (orderClient) => orderClient.participantRelation === OrderClientRelation.TITULAR
                                ).length;

                                selectedProduct.order.clients.map((orderClient) => {
                                    orderClient.isSoleHolder = countHolders === 1;
                                });
                            }
                        });

                        return Transaction.success(selectedProducts);
                    })
                ),
            (throwable) => of(Transaction.error(throwable.message))
        );
    });

@Injectable()
export class ProductProvider {
    constructor(
        private orderApiRepository: OrderApiRepository,
        private productApiRepository: ProductApiRepository,
        private productCacheRepository: ProductCacheRepository,
        private productDetailCacheRepository: ProductDetailCacheRepository,
        private selectedProductCacheRepository: SelectedProductCacheRepository,
        private productByOrderTypeCacheRepository: ProductByOrderTypeCacheRepository,
    ) {}

    find(): Observable<Transaction<ProductInterface[]>> {
        const cacheId = this.productCacheRepository.getKey();

        return concat(
            this.productCacheRepository.load(cacheId),
            this.productApiRepository.load().pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.productCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());
    }

    findMine(contractCode: IdInterface) {
        const cacheId = this.productCacheRepository.getKey(true);

        return concat(
            this.productCacheRepository.load(cacheId),
            this.productApiRepository.loadMine(contractCode).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.productCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());
    }

    detail(productCode: IdInterface): Observable<Transaction<ProductInterface>> {
        const cacheId = this.productDetailCacheRepository.getKey(productCode);

        return concat(
            this.productDetailCacheRepository.load(cacheId),
            this.productApiRepository.loadDetail(productCode).pipe(
                switchMap(
                    (transaction: Transaction<ProductInterface>): Observable<Transaction<ProductInterface>> =>
                        transaction.map(
                            (product) =>
                                this.productApiRepository.loadDocument(productCode).pipe(
                                    map((transactionDocument) => {
                                        transactionDocument.fold((document) => (product.documents.costAndTaxes = document));

                                        return Transaction.success(product);
                                    })
                                ),
                            (throwable) => of(Transaction.error(throwable.message))
                        )
                ),
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.productDetailCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());
    }

    findSelected(contractCode: IdInterface, clientCode?: IdInterface): Observable<Transaction<SelectedProductInterface[]>> {
        return concat(
            this.selectedProductCacheRepository.load(),
            this.productApiRepository.loadSelected(contractCode, clientCode).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.selectedProductCacheRepository.save(transaction.data).subscribe();
                    }
                })
            )
        ).pipe(
            first(),
            selectedProductsMap(this),
            switchMap(
                (transaction: Transaction<SelectedProductInterface[]>): Observable<Transaction<SelectedProductInterface[]>> =>
                    transaction.map(
                        (selectedProducts) =>
                            this.orderApiRepository.signedDocuments(contractCode).pipe(
                                map((transactionSigned) => {
                                    transactionSigned.fold((documents) =>
                                        selectedProducts.forEach((selectedProduct) => {
                                            selectedProduct.documents = documents.filter(
                                                (document) =>
                                                    document.selectedProductId && document.selectedProductId.equals(selectedProduct.id)
                                            );
                                        })
                                    );

                                    return Transaction.success(selectedProducts);
                                })
                            ),
                        (throwable) => of(Transaction.error(throwable.message))
                    )
            )
        );
    }

    checkSelected(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<Transaction<SelectedProductInterface[]>> {
        return this.productApiRepository.checkSelected(contractCode, products).pipe(selectedProductsMap(this));
    }

    addSelected(contractCode: IdInterface, products: ProductInterface[]): Observable<Transaction<SelectedProductInterface[]>> {
        return this.productApiRepository.addSelected(contractCode, products).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.selectedProductCacheRepository.clear().subscribe();
                }
            })
        );
    }

    updateSelected(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<Transaction<void>> {
        return this.productApiRepository.updateSelected(contractCode, products).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.selectedProductCacheRepository.clear().subscribe();
                }
            })
        );
    }

    removeSelected(product: SelectedProductInterface): Observable<Transaction<void>> {
        return this.productApiRepository.removeSelected(product).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.selectedProductCacheRepository.clear().subscribe();
                }
            })
        );
    }


    findByOrderType(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string): Observable<Transaction<ProductInterface[]>> {


        return concat(
            this.productApiRepository.loadByOrderType(portfolioCode, clientCode, distributionChannel, orderType, riskLevel).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.productByOrderTypeCacheRepository.save(transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());


    }

    findByOrderTypeClient(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string): Observable<Transaction<ProductInterface[]>> {


        return concat(
            this.productApiRepository.loadByOrderTypeClient(portfolioCode, clientCode, distributionChannel, orderType, riskLevel).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.productByOrderTypeCacheRepository.save(transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());


    }
}
